import React, { useState, useMemo } from 'react';
import {
  Box,
  List,
  TextField,
  Typography,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSessionStore } from '@/stores/useSessionStore';
import { isToday, isYesterday } from 'date-fns';
import SessionItem from './SessionItem';

/**
 * 会话列表组件，按日期分组并支持搜索过滤。
 */
const SessionList: React.FC = () => {
  const sessions = useSessionStore((s) => s.sessions);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const query = searchQuery.toLowerCase();
    return sessions.filter((s) => {
      const ownerName = s.user_display_name || s.user_name || '';
      return [s.title, s.session_id, ownerName].some((value) => value.toLowerCase().includes(query));
    });
  }, [sessions, searchQuery]);

  // 按日期分组
  const grouped = useMemo(() => {
    const groups: Record<string, typeof sessions> = {
      today: [],
      yesterday: [],
      earlier: [],
    };

    for (const session of filteredSessions) {
      const date = new Date(session.created_at);
      if (isToday(date)) {
        groups.today.push(session);
      } else if (isYesterday(date)) {
        groups.yesterday.push(session);
      } else {
        groups.earlier.push(session);
      }
    }

    return groups;
  }, [filteredSessions]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* 搜索框 */}
      <TextField
        fullWidth
        size="small"
        placeholder="搜索会话..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'background.default',
            borderRadius: 2,
          },
        }}
      />

      {/* 列表 */}
      <List dense sx={{ py: 0 }}>
        {grouped.today.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                color: 'text.secondary',
                fontWeight: 600,
                display: 'block',
              }}
            >
              今天
            </Typography>
            {grouped.today.map((session) => (
              <SessionItem key={session.session_id} session={session} />
            ))}
          </Box>
        )}

        {grouped.yesterday.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                color: 'text.secondary',
                fontWeight: 600,
                display: 'block',
              }}
            >
              昨天
            </Typography>
            {grouped.yesterday.map((session) => (
              <SessionItem key={session.session_id} session={session} />
            ))}
          </Box>
        )}

        {grouped.earlier.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                color: 'text.secondary',
                fontWeight: 600,
                display: 'block',
              }}
            >
              更早
            </Typography>
            {grouped.earlier.map((session) => (
              <SessionItem key={session.session_id} session={session} />
            ))}
          </Box>
        )}

        {filteredSessions.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              py: 3,
            }}
          >
            {searchQuery ? '未找到匹配的会话' : '暂无会话'}
          </Typography>
        )}
      </List>
    </Box>
  );
};

export default SessionList;
